import { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { submitContact } from '@/lib/api';
import { Seo } from '@/components/Seo';

export const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            await submitContact(formData);
            toast.success("Message sent! We'll be in touch soon.");
            setFormData({ name: '', email: '', message: '' });
        } catch (err) {
            const msg = err.response?.data?.error?.message || 'Something went wrong. Please try again.';
            toast.error(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <Seo
                title="Contact CatchingOn — Get a Marketing Quote"
                description="Get in touch with CatchingOn for a free, no-obligation chat about your marketing. Copywriting, content and paid social for UK businesses."
                path="/contact"
            />
            {/* Hero Section */}
            <section className="pt-32 pb-20 bg-brand-periwinkle min-h-screen px-6 md:px-12" data-testid="contact-hero">
                <div className="max-w-3xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12"
                    >
                        <p className="text-sm font-bold tracking-widest text-black/60 mb-6">LETS CHAT</p>
                        <h1 className="text-4xl sm:text-5xl font-black text-black leading-tight mb-6">
                            Want a quote? Have a question? Just want a natter?
                        </h1>
                        <p className="text-lg text-black/80">
                            Get in touch below
                        </p>
                    </motion.div>

                    <motion.form 
                        onSubmit={handleSubmit} 
                        className="space-y-6"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        data-testid="contact-form"
                    >
                        <div>
                            <label htmlFor="name" className="block text-sm font-bold text-black mb-2">
                                Name
                            </label>
                            <input
                                id="name"
                                name="name"
                                type="text"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 bg-white border-2 border-black focus:outline-none focus:ring-2 focus:ring-brand-lime"
                                data-testid="contact-name-input"
                            />
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-bold text-black mb-2">
                                Email Address:
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 bg-white border-2 border-black focus:outline-none focus:ring-2 focus:ring-brand-lime"
                                data-testid="contact-email-input"
                            />
                        </div>

                        <div>
                            <label htmlFor="message" className="block text-sm font-bold text-black mb-2">
                                Comments:
                            </label>
                            <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                required
                                rows={6}
                                className="w-full px-4 py-3 bg-white border-2 border-black focus:outline-none focus:ring-2 focus:ring-brand-lime resize-none"
                                data-testid="contact-message-input"
                            />
                        </div>

                        <div className="text-center pt-4">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="btn-lime disabled:opacity-50"
                                data-testid="contact-submit-btn"
                            >
                                {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
                            </button>
                        </div>

                        <p className="text-sm text-black/60 mt-4 text-center">
                            We use your details to respond to your enquiry and will retain
                            them for up to 12 months. See our{' '}
                            <a href="/privacy" className="text-black font-bold underline hover:text-brand-lime transition-colors">
                                Privacy Policy
                            </a>{' '}
                            for full details.
                        </p>
                    </motion.form>
                </div>
            </section>
        </div>
    );
};

export default Contact;
